import {
  startRegistration,
  startAuthentication,
  WebAuthnAbortService,
} from '@simplewebauthn/browser';
import { browserSupportsWebAuthn, platformAuthenticatorIsAvailable } from '@simplewebauthn/browser';
import apiClient from '../api/apiClient';

// Client Hints auslesen (für korrekte OS-Erkennung bei Login-Tracking)
async function getClientHints(): Promise<Record<string, string> | undefined> {
  try {
    const nav = navigator as any;
    if (nav.userAgentData?.getHighEntropyValues) {
      const data = await nav.userAgentData.getHighEntropyValues(['platformVersion', 'platform', 'fullVersionList', 'model']);
      const hints: Record<string, string> = {};
      if (data.platformVersion) hints.platformVersion = data.platformVersion;
      if (data.platform) hints.platform = data.platform;
      if (data.model) hints.model = data.model;
      if (data.fullVersionList) {
        hints.fullVersionList = data.fullVersionList.map((b: { brand: string; version: string }) => `"${b.brand}";v="${b.version}"`).join(', ');
      }
      return hints;
    }
    // Safari/Firefox Fallback
    const hints: Record<string, string> = {};
    hints.maxTouchPoints = String(navigator.maxTouchPoints || 0);
    if (/iPhone/.test(navigator.userAgent)) hints.detectedPlatform = 'iOS';
    else if (/Macintosh/.test(navigator.userAgent) && navigator.maxTouchPoints > 1) hints.detectedPlatform = 'iPadOS';
    return hints;
  } catch { return undefined; }
}

// ============================================================================
// RENDITEFUCHS v3 - Passkey Service Types
// ============================================================================
// Migration: INTEGER IDs -> UUID (string)
// ============================================================================

export interface Passkey {
  id: string;  // UUID (v3 Migration)
  device_name: string;
  device_type: 'singleDevice' | 'multiDevice' | 'platform' | 'cross-platform';
  transports: string[];
  created_at: string;
  last_used_at: string | null;
  aaguid: string;
}

export class PasskeyService {

  // ============================================================
  // 1. BROWSER SUPPORT CHECK
  // ============================================================

  static async isBrowserSupported(): Promise<boolean> {
    return browserSupportsWebAuthn();
  }

  static async isPlatformAuthenticatorAvailable(): Promise<boolean> {
    try {
      return await platformAuthenticatorIsAvailable();
    } catch {
      return false;
    }
  }

  static async getCapabilities() {
    const webauthnSupported = await this.isBrowserSupported();
    const platformAuthAvailable = await this.isPlatformAuthenticatorAvailable();

    let authenticatorType = 'none';
    if (platformAuthAvailable) {
      // Touch ID (Mac), Face ID (iPhone), Windows Hello
      const userAgent = navigator.userAgent.toLowerCase();
      if (userAgent.includes('mac') || userAgent.includes('iphone') || userAgent.includes('ipad')) {
        authenticatorType = userAgent.includes('iphone') || userAgent.includes('ipad')
          ? 'Face ID / Touch ID'
          : 'Touch ID';
      } else if (userAgent.includes('windows')) {
        authenticatorType = 'Windows Hello';
      } else {
        authenticatorType = 'Platform Authenticator';
      }
    }

    return {
      supported: webauthnSupported,
      platformAuthenticator: platformAuthAvailable,
      authenticatorType,
    };
  }

  // ============================================================
  // 2. REGISTRATION
  // ============================================================

  static async registerPasskey(deviceName?: string): Promise<void> {
    try {
      // 1. Options vom Server holen
      const optionsResponse = await apiClient.post('/passkeys/register/options');
      const options = optionsResponse.data;

      // 2. Browser WebAuthn API aufrufen (v13+ API: optionsJSON)
      const response = await startRegistration({ optionsJSON: options });

      // 3. Response an Server senden
      await apiClient.post('/passkeys/register/verify', {
        response,
        deviceName,
      });
    } catch (error: any) {
      console.error('Passkey registration error:', error);

      // User-freundliche Fehlermeldungen
      if (error.name === 'NotAllowedError') {
        throw new Error('Passkey-Registrierung wurde abgebrochen');
      } else if (error.name === 'InvalidStateError') {
        throw new Error('Dieser Passkey ist bereits registriert');
      } else if (error.name === 'NotSupportedError') {
        throw new Error('Passkeys werden von diesem Browser nicht unterstützt');
      } else if (error.response?.data?.message) {
        throw new Error(error.response.data.message);
      } else {
        throw new Error('Passkey-Registrierung fehlgeschlagen');
      }
    }
  }

  // ============================================================
  // 3. AUTHENTICATION
  // ============================================================

  // v3 Migration: user.id is now UUID (string)
  static async loginWithPasskey(email?: string): Promise<{
    token: string;
    user: { id: string; email: string };
  }> {
    try {
      // Eine evtl. laufende Conditional-UI-Ceremony abbrechen, damit der Browser
      // garantiert die neue Challenge verwendet und nicht eine ältere signiert.
      this.abortConditionalAuthentication();

      // 1. Options vom Server holen
      const optionsResponse = await apiClient.post('/passkeys/authenticate/options', {
        email,
      });
      const options = optionsResponse.data;

      // 2. Browser WebAuthn API aufrufen (v13+ API: optionsJSON)
      const response = await startAuthentication({ optionsJSON: options });

      // 3. Response an Server senden (mit Client Hints für Login-Tracking)
      const clientHints = await getClientHints();
      const verifyResponse = await apiClient.post('/passkeys/authenticate/verify', {
        response,
        clientHints,
      });

      return verifyResponse.data;
    } catch (error: any) {
      console.error('Passkey authentication error:', error);

      // User-freundliche Fehlermeldungen
      if (error.name === 'NotAllowedError') {
        throw new Error('Passkey-Login wurde abgebrochen');
      } else if (error.name === 'NotSupportedError') {
        throw new Error('Passkeys werden von diesem Browser nicht unterstützt');
      } else if (error.response?.data?.message) {
        const message = error.response.data.message;
        // User-friendly message for "No passkeys registered"
        if (message.includes('No passkeys registered') || message.includes('User not found')) {
          throw new Error('Für diesen Account sind noch keine Passkeys registriert. Bitte melde dich mit deinem Passwort an und füge einen Passkey in den Profil-Einstellungen hinzu.');
        }
        throw new Error(message);
      } else {
        throw new Error('Passkey-Login fehlgeschlagen');
      }
    }
  }

  // ============================================================
  // 3b. CONDITIONAL UI AUTHENTICATION (Autofill)
  // ============================================================
  // Startet Passkey-Authentifizierung im Hintergrund für Autofill-UI
  // Der Browser zeigt Passkeys als Vorschläge im Eingabefeld an

  private static conditionalAbortController: AbortController | null = null;

  static async startConditionalAuthentication(
    onSuccess: (result: { token: string; user: { id: string; email: string } }) => void,
    onError?: (error: Error) => void
  ): Promise<void> {
    try {
      // Vorherige conditional authentication abbrechen falls aktiv
      this.abortConditionalAuthentication();

      // Neuen AbortController erstellen
      this.conditionalAbortController = new AbortController();

      // 1. Options vom Server holen (ohne Email = Discoverable Credentials)
      const optionsResponse = await apiClient.post('/passkeys/authenticate/options', {});
      const options = optionsResponse.data;

      // 2. Browser WebAuthn API mit Conditional UI aufrufen (v13+ API)
      // useBrowserAutofill: true aktiviert die Autofill-Integration
      const response = await startAuthentication({
        optionsJSON: options,
        useBrowserAutofill: true,
      });

      // 3. Response an Server senden (mit Client Hints für Login-Tracking)
      const clientHints = await getClientHints();
      const verifyResponse = await apiClient.post('/passkeys/authenticate/verify', {
        response,
        clientHints,
      });

      // Erfolg!
      onSuccess(verifyResponse.data);
    } catch (error: any) {
      // AbortError ignorieren (normales Abbrechen)
      if (error.name === 'AbortError') {
        return;
      }

      // NotAllowedError ignorieren (User hat abgebrochen oder anderer Auth gestartet)
      if (error.name === 'NotAllowedError') {
        return;
      }

      // Challenge-Mismatch ignorieren (passiert wenn Session abgelaufen oder Seite neu geladen)
      // Das ist bei Conditional UI normal und kein echter Fehler
      const errorMessage = error.response?.data?.message || '';
      if (errorMessage.includes('Unexpected authentication response challenge')) {
        return;
      }

      console.error('Conditional authentication error:', error);

      if (onError) {
        onError(error);
      }
    }
  }

  static abortConditionalAuthentication(): void {
    if (this.conditionalAbortController) {
      this.conditionalAbortController.abort();
      this.conditionalAbortController = null;
    }
    // SimpleWebAuthn nutzt intern einen eigenen AbortController für die WebAuthn-Ceremony.
    // Nur cancelCeremony() bricht eine laufende Conditional-UI tatsächlich ab.
    WebAuthnAbortService.cancelCeremony();
  }

  // ============================================================
  // 4. CHECK USER HAS PASSKEY (for 2-step login)
  // ============================================================

  static async checkUserHasPasskey(identifier: string): Promise<{
    exists: boolean;
    hasPasskey: boolean;
  }> {
    try {
      const response = await apiClient.post('/passkeys/check', { identifier });
      return response.data;
    } catch (error) {
      console.error('Error checking passkey status:', error);
      return { exists: false, hasPasskey: false };
    }
  }

  // ============================================================
  // 5. MANAGEMENT
  // ============================================================

  static async listPasskeys(): Promise<Passkey[]> {
    const response = await apiClient.get('/passkeys/list');
    return response.data;
  }

  // v3 Migration: id is now UUID (string)
  static async deletePasskey(id: string): Promise<void> {
    await apiClient.delete(`/passkeys/${id}`);
  }

  // v3 Migration: id is now UUID (string)
  static async renamePasskey(id: string, name: string): Promise<void> {
    await apiClient.put(`/passkeys/${id}/rename`, { name });
  }
}
